import { useCallback, useEffect, useRef, useState } from 'react';
import { getQuoteVersion } from '../quote.api';
import { editorFromQuote } from '../quoteEditorModel';
import { quoteConflictMessage } from '../quoteRevision';

export default function useQuoteRevisionConflict({ editor, setEditor, setSaving, setError, onReloaded }) {
  const [revisionConflict, setRevisionConflict] = useState(null);
  const currentEditor = useRef(editor);
  currentEditor.current = editor;
  useEffect(() => {
    setRevisionConflict((previous) => previous && (previous.quoteId !== editor?.id
      || previous.quoteVersionId !== editor?.quoteVersionId) ? null : previous);
  }, [editor?.id, editor?.quoteVersionId]);
  useEffect(() => {
    if (!editor?.serverWriteConflict) return;
    setRevisionConflict(editor.serverWriteConflict);
    setError('La commande a réussi sur le serveur. Vos nouvelles saisies ont été conservées. Rechargez explicitement la version avant une nouvelle écriture.');
  }, [editor?.serverWriteConflict, setError]);

  const reportRevisionError = useCallback((error, fallback) => {
    const code = error.body?.code;
    if (['QUOTE_REVISION_CONFLICT', 'QUOTE_REVISION_REQUIRED', 'QUOTE_REVISION_INVALID', 'QUOTE_LOCAL_CHANGES_DURING_WRITE'].includes(code)) {
      setRevisionConflict({ quoteId: error.body?.quoteId ?? editor?.id,
        quoteVersionId: error.body?.quoteVersionId ?? editor?.quoteVersionId });
      setError(code === 'QUOTE_REVISION_CONFLICT' ? quoteConflictMessage : error.message);
    } else setError(error.message || fallback);
  }, [editor?.id, editor?.quoteVersionId, setError]);

  const reloadAfterConflict = useCallback(async () => {
    if (!revisionConflict || !window.confirm('Recharger la version actuelle supprimera vos modifications locales non enregistrées. Confirmer le rechargement ?')) return;
    setSaving(true);
    const submitted = currentEditor.current;
    try {
      const quote = await getQuoteVersion(revisionConflict.quoteId, revisionConflict.quoteVersionId);
      if (currentEditor.current !== submitted) {
        setError('Vos nouvelles saisies ont été conservées. Confirmez à nouveau le rechargement si vous souhaitez les abandonner.');
        return;
      }
      setEditor(editorFromQuote(quote));
      onReloaded?.();
      setRevisionConflict(null);
      setError(null);
    } catch (error) { setError(error.message); }
    finally { setSaving(false); }
  }, [revisionConflict, setEditor, setSaving, setError, onReloaded]);

  return { revisionConflict, reportRevisionError, reloadAfterConflict };
}
